import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
// This import is used in the JsDoc to generate a link to the RoleGroup class
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PopupHttpErrors } from 'src/app/decorators/http-error/popup-http-errors.decorator';
import { RoleRepository } from '../repositories/role.repository';
import { ERole } from 'src/app/shared/roles/models/role.enum';

@Injectable({
  providedIn: 'root'
})
export class RoleService {

  private grantedRoles: Array<ERole>;

  constructor(private roleRepository: RoleRepository) { }

  /**
   * Needs to run before any of the other methods in this class or they will throw errors. Currently this is invoked
   * via Angular APP_INITIALIZER to guarantee this is run before the rest of the app begins execution. It is not
   * intended to be used outside of app initialization as there are no safeguards in place for state management.
   */
  public loadRoles(): Observable<void> {
    return this.getRoles().pipe(
      tap(roles => this.grantedRoles = roles),
      switchMap(() => EMPTY)
    );
  }

  /**
   * Checks if the current user has the specified role. Use convenience class Roles for valid roles to pass in.
   * i.e. roleService.hasRole(ERole.EDIT_COMMENTS)
   * @param role
   * @returns true if the current user's role(s) list contains the specified role
   * @see {@link ERole}
   */
  public hasRole(role: ERole): boolean {
    return this.grantedRoles.includes(role);
  }

  /**
   * Checks if the current user has at least one of the given roles. Use convenience class RoleGroup for commonly used
   * collections of ERole.
   * i.e. roleService.hasAnyRole(RoleGroup.STUDENT_ROLE_GROUP)
   * or roleService.hasAnyRole([ERole.EDIT_COMMENTS, ERole.IR_SUBMIT])
   * @param roles
   * @returns true if the current user's role(s) list contains at least 1 of the specified roles
   * @see {@link ERole}
   * @see {@link RoleGroup}
   */
  public hasAnyRole(roles: Array<ERole>): boolean {
    return roles.some(role => this.grantedRoles.includes(role));
  }

  /**
   * Checks if the current user has all of the given roles. Use convenience class RoleGroup for commonly used
   * collections of ERole.
   * i.e. roleService.hasAllRoles(RoleGroup.STUDENT_ROLE_GROUP)
   * or roleService.hasAllRoles([ERole.EDIT_COMMENTS, ERole.IR_SUBMIT])
   * @param roles
   * @see {@link ERole}
   * @see {@link RoleGroup}
   */
  public hasAllRoles(roles: Array<ERole>): boolean {
    return roles.every(role => this.grantedRoles.includes(role));
  }

  @PopupHttpErrors()
  private getRoles(): Observable<Array<ERole>> {
    return this.roleRepository.getRoles();
  }
}
